import { fetcher } from "@/helpers/fetcher";
import { CodeValue } from "@/types/common";
import { DepartmentRequest, DepartmentTreeNode } from "@/types/department";
import { UserDepartmentPositionResponse } from "@/types/user";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const useApproverPositionCodes = () => {
  return useQuery<CodeValue[]>({
    queryKey: ["approver-position-codes"],
    queryFn: async ({ signal }: { signal: AbortSignal }): Promise<CodeValue[]> =>
      fetcher({ url: "/approver/departments/position-codes", signal }),
  });
};

export const useApproverDepartmentHierarchy = (searchTerm?: string, isApproval?: boolean) => {
  return useQuery<DepartmentTreeNode[]>({
    queryKey: ["approver-department-hierarchy", searchTerm, isApproval],
    queryFn: async ({ signal }): Promise<DepartmentTreeNode[]> =>
      fetcher({
        url: "/approver/departments",
        signal,
        params: { search: searchTerm ?? null, isApproval: isApproval ? 1 : null },
      }),
  });
};

export const useApproverDepartmentUsers = (departmentId: number) => {
  return useQuery<UserDepartmentPositionResponse[]>({
    queryKey: ["approver-department-users", departmentId],
    queryFn: async ({ signal }): Promise<UserDepartmentPositionResponse[]> =>
      fetcher({ url: `/approver/departments/${departmentId}/users`, signal }),
    placeholderData: (previous) => previous,
  });
};

export const useDepartmentUsersUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ departmentId, users }: { departmentId: number; users: UserDepartmentPositionResponse[] }) =>
      fetcher({ url: `/approver/departments/${departmentId}/users`, method: "PUT", data: users }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["approver-department-users"] });
    },
  });
};

export const useDepartmentUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ departmentId, request }: { departmentId: number; request: DepartmentRequest }) =>
      fetcher({ url: `/approver/departments/${departmentId}`, method: "PATCH", data: request }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["approver-department-hierarchy"] });
    },
  });
};

export const userDepartmentCreate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ parentId, request }: { parentId: number | null; request: DepartmentRequest }) =>
      fetcher({ url: `/approver/departments`, method: "POST", params: { parentId: parentId ?? null }, data: request }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["approver-department-hierarchy"] });
    },
  });
};

export const userDepartmentDelete = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ departmentId }: { departmentId: number }) =>
      fetcher({ url: `/approver/departments/${departmentId}`, method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["approver-department-hierarchy"] });
    },
  });
};
